import {
  first,
  isEmpty,
  map,
  reduce,
} from 'lodash';
import * as React from 'react';

import { logger } from '@common';
import { useAddCustomWorkletsMutation, useAddCustomStagesMutation } from '@frontend/app/hooks';
import { TWorklet } from '@frontend/app/containers/Projects/types';
import { StageType } from '@frontend/app/types/globalTypes';
import { IProgramStage } from '@services/custom-stages';

type TPartialWorklets = Partial<Pick<TWorklet, 'specKey' | 'specTitle'>>;
const { useState } = React;

interface IUseAddWorklets {
  addWorklets: (worklets: TPartialWorklets[], isFlexibleProject?: boolean) => Promise<string[] | IProgramStage[]>,
  loading: boolean,
}

/**
 * Adds worklets for specs that don't have specKeys
 * For regular projects, creates worklets via AddCustomWorkletsMutation and returns string[]
 * For flexible projects, creates custom stages via AddCustomStagesMutation and returns IProgramStage[]
 * @returns addWorklets: Promise to add worklets where it returns either:
 *                       - a list of worklet specKeys (for regular projects)
 *                       - a list of program stages with type and id (for flexible projects)
 * @returns loading
 */
export const useAddWorklets = (programId?: number): IUseAddWorklets => {
  const [loading, setLoading] = useState(false);

  const [
    addCustomWorklets,
    { loading: isAddingWorklets },
  ] = useAddCustomWorkletsMutation({
    onCompleted: () => {
      setLoading(false);
    },
    onError: (error) => {
      logger.error(error);
      setLoading(false);
    },
  });

  const [
    addCustomStages,
    { loading: isAddingStages },
  ] = useAddCustomStagesMutation({
    onCompleted: () => {
      setLoading(false);
    },
    onError: (error) => {
      logger.error(error);
      setLoading(false);
    },
  });

  const addWorklets = (
    worklets: TPartialWorklets[],
    isCustomStagesEnabled?: boolean,
  ) => (
    new Promise<string[] | IProgramStage[]>((resolve, reject) => {
      setLoading(true);

      // Grab task names for new worklets
      const taskNames = reduce<TPartialWorklets, string[]>(
        worklets,
        (result, worklet) => {
          if (!worklet.specKey && worklet.specTitle) {
            result.push(worklet.specTitle);
          }
          return result;
        },
        [],
      );

      if (isEmpty(taskNames)) {
        logger.debug('No new worklets to add from:', { worklets });
        if (isCustomStagesEnabled) {
          resolve(map(worklets, (worklet) => ({
            id: worklet.specKey!,
            type: StageType.DEFAULT
          })));
        } else {
          resolve(map(worklets, (worklet) => worklet.specKey));
        }
        return;
      }

      logger.debug('Adding new worklets with task names:', { taskNames });

      if (isCustomStagesEnabled) {
        // Flexible project flow - create custom stages
        // Create stages sequentially since we need to use createStage mutation
        Promise.all(
          taskNames.map((name) =>
            addCustomStages({
              variables: {
                data: {
                  name,
                  programId,
                  type: StageType.CUSTOM
                }
              }
            }).then((result) => result?.data?.createStage?.id?.toString())
          )
        )
        .then((stageIds) => {
          const newStageIds = stageIds.filter(Boolean);
          const result = map(worklets, (worklet) => (
            worklet.specKey
              ? { id: worklet.specKey, type: StageType.DEFAULT }
              : { id: first(newStageIds.splice(0, 1))!, type: StageType.CUSTOM }
          ));
          setLoading(false);
          resolve(result);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
      } else {
        // Regular project flow - create worklets
        addCustomWorklets({
          variables: {
            taskNames,
          },
        })
        .then((result) => result?.data?.specKeys)
        .then((specKeys) => {
          const newSpecKeys = [...specKeys];
          const result = map(worklets, (worklet) => (
            worklet.specKey
              ? worklet.specKey
              : first(newSpecKeys.splice(0, 1))
          ));
          setLoading(false);
          resolve(result);
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
      }
    })
  );

  return {
    addWorklets,
    loading: loading || isAddingWorklets || isAddingStages,
  };
};
